<template>
  <div class="c-product-overview__item">
    <a
      class="c-product-overview__link"
      :href="pageUrl"
      :title="product.document.baseShape"
      @click="trackEvent"
    >
      <picture class="c-product-overview__link--image">
        <vue-load-image>
          <img
            slot="image"
            :src="imageSrc"
            :alt="product.document.baseShape"
            :width="images.dimensions.width"
            :height="images.dimensions.height"
            loading="lazy"
          />
          <loader slot="preloader" class="c-product-overview__loader" />
        </vue-load-image>

        <orderable-label v-if="product.document.orderable" />
      </picture>

      <div class="c-product-overview__link--content flex-grow-1">
        <span class="h4">
          {{ product.document.baseShape }}
        </span>

        <p
          class="c-product-overview__link--text"
          v-html="product.document.teaserDescription"
        />
      </div>
    </a>

    <customize-label v-if="isVisibleToolAdvisorUrl" :link="toolAdvisorUrl" />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import VueLoadImage from 'vue-load-image';
import Loader from './loader.vue';
import CustomizeLabel from './customize-label.vue';
import OrderableLabel from './orderable-label.vue';
import { gtagBaseShapesMapper } from '@modules/../util/helper.js';

export default {
  components: {
    VueLoadImage,
    Loader,
    CustomizeLabel,
    OrderableLabel
  },
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      images: {
        requested: false,
        dimensions: {
          height: 170,
          width: 350
        },
        sources: {
          drawing: undefined,
          teaser: undefined
        }
      },
      pageUrl: undefined
    };
  },
  computed: {
    ...mapState(['culture']),
    ...mapState('productSearch', ['imageType', 'toolAdvisorBaseUrl']),
    fallbackImage() {
      return '';
    },
    imageSrc() {
      const imageSrc =
        this.imageType === 'teaser'
          ? this.images.sources.teaser
          : this.images.sources.drawing;
      if (!imageSrc && this.images.requested) return this.fallbackImage;
      return imageSrc;
    },
    isVisibleToolAdvisorUrl() {
      return (
        this.product.document.configurable === 'specials' && this.toolAdvisorUrl
      );
    },
    toolAdvisorUrl() {
      if (!this.toolAdvisorBaseUrl) return undefined;

      return `${this.toolAdvisorBaseUrl}?toolID=${this.product.document.baseShape}`;
    }
  },
  async created() {
    await this.getImages();
  },
  methods: {
    async getImages() {
      try {
        const { data } = await this.axios.get(
          `/Umbraco/Api/Products/GetUrls/${this.culture}/${this.images.dimensions.width}/${this.images.dimensions.height}`,
          {
            params: {
              productName: encodeURIComponent(this.product.document.baseShape)
            }
          }
        );
        this.images.requested = true;

        this.images.sources.drawing = data.technicalImageUrl;
        this.images.sources.teaser = data.productImageUrl;
        this.pageUrl = data.productPageUrl;
      } catch (err) {
        if (err.response.status !== 404) {
          throw err;
        }
      }
    },
    trackEvent() {
      try {
        window.gtag('event', 'select_item', {
          item_list_name: 'overview',
          items: gtagBaseShapesMapper([this.product.document])
        });
      } catch(err) {
        console.error('Error while tracking conversion', err);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/css/src/utilities/_variables';
@import '@/css/src/utilities/_mixins';
@import '@/css/src/utilities/_functions';
@import '~bootstrap/scss/mixins';

.c-product-overview {
  &__item {
    position: relative;
    display: flex;
    width: 100%;
  }

  &__link {
    display: flex;
    flex-direction: column;
    min-width: 0;
    position: relative;
    overflow: hidden;
    width: 100%;

    @include remove-text-decoration($body-color);

    @include hover-corner-icon();

    &:hover {
      .c-product-overview__link--image {
        background-color: $white;

        img {
          transform: scale(1.2, 1.2);
        }
      }
    }

    &--image {
      background-color: #f8f8f8;
      padding: 20px 0;
      display: flex;
      justify-content: center;
      transition: background-color $transition-default-duration
        $transition-default-timing;
      min-height: 210px;
      align-items: center;
      position: relative;

      img {
        mix-blend-mode: multiply;
        max-height: 150px;
        object-fit: scale-down;
        transition: transform $transition-default-duration
          $transition-default-timing;
      }
    }

    &--content {
      padding: 30px;
      background-color: $white;
    }

    &--text {
      font-size: rem(14);
      margin-bottom: unset;
    }

    &--loader {
      display: flex;
      align-items: center;
    }
  }
}
</style>
